/**
 * Helpers puros dos Relatórios Financeiros.
 * Mantidos livres de React/serviços para permitir testes unitários (vitest).
 */
import type { DreLine, DreSubtotals, ReportBasis } from '@/types/financialReports';

// ─── Percentuais ────────────────────────────────────────────────

/**
 * Variação percentual de `curr` em relação a `prev`.
 * Retorna `null` quando `prev` é 0 (variação indefinida).
 */
export const pctDelta = (curr: number, prev: number): number | null => {
  if (prev === 0) return null;
  return ((curr - prev) / Math.abs(prev)) * 100;
};

/** Formata percentual pt-BR com 1 casa: 12.34 → "12,3%". */
export const formatPct = (value: number, digits = 1): string =>
  `${value.toFixed(digits).replace('.', ',')}%`;

/**
 * Percentual de `part` sobre `total` (ex.: despesas sobre receita).
 * Retorna `null` quando `total` é 0 (percentual indefinido).
 */
export const pctOf = (part: number, total: number): number | null => {
  if (total === 0) return null;
  return (part / Math.abs(total)) * 100;
};

// ─── Meses ──────────────────────────────────────────────────────

const MONTH_ABBR_PTBR = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/** Converte "2026-06" em "jun/26" (sem parsing via Date — não desloca fuso). */
export const monthLabelPtBR = (yearMonth: string): string => {
  const match = /^(\d{4})-(\d{2})/.exec(yearMonth);
  if (!match) return yearMonth;
  const monthIdx = Number(match[2]) - 1;
  if (monthIdx < 0 || monthIdx > 11) return yearMonth;
  return `${MONTH_ABBR_PTBR[monthIdx]}/${match[1].slice(2)}`;
};

// ─── Competência (realizado × projetado) ────────────────────────

const MONTH_FULL_PTBR = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

export interface Competence {
  year: number;
  month: number;
}

/**
 * Competência corrente. `today` entra por parâmetro para os testes serem
 * determinísticos — e porque o resto do módulo evita `new Date(iso)`.
 */
export const currentCompetence = (today: Date = new Date()): Competence => ({
  year: today.getFullYear(),
  month: today.getMonth() + 1,
});

/** A competência (year, month) é posterior à de referência? */
export const isFutureCompetence = (year: number, month: number, ref: Competence): boolean =>
  year > ref.year || (year === ref.year && month > ref.month);

/** O ano inteiro começa depois da competência de referência? */
export const isFutureYear = (year: number, ref: Competence): boolean =>
  isFutureCompetence(year, 1, ref);

/** "setembro/2026" — sem passar por Date, que deslocaria o mês em UTC−3. */
export const competenceLabelPtBR = (year: number, month: number): string => {
  const idx = month - 1;
  if (idx < 0 || idx > 11) return String(year);
  return `${MONTH_FULL_PTBR[idx]}/${year}`;
};

/**
 * Percentual das despesas sobre a receita. `null` quando a receita não é
 * comparável (competência projetada): o denominador seriam apenas alguns
 * recebimentos avulsos já lançados, e o resultado sairia na casa dos milhares.
 */
export const revenueSharePct = (
  totalExpenses: number,
  totalRevenue: number,
  revenueAvailable: boolean,
): number | null => {
  if (!revenueAvailable) return null;
  return pctOf(totalExpenses, totalRevenue);
};

/**
 * Texto do estado vazio. Distingue "não há lançamentos" de "a competência
 * ainda não aconteceu" — era exatamente essa diferença que a mensagem genérica
 * escondia quando o usuário consultava setembro ainda em agosto.
 *
 * `fallback` é a frase de sempre, usada quando o período já passou.
 */
export const emptyPeriodMessage = (
  year: number,
  month: number | null,
  basis: ReportBasis,
  ref: Competence,
  fallback: string,
): string => {
  const future = month == null ? isFutureYear(year, ref) : isFutureCompetence(year, month, ref);
  if (future && basis === 'realizado') {
    return 'Competência futura — este relatório mostra apenas o realizado.';
  }
  return fallback;
};

// ─── DRE — linhas de exibição ───────────────────────────────────

export type DreRowKind = 'line' | 'subtotal' | 'memoHeader';

export interface DreDisplayRow {
  kind: DreRowKind;
  code: string;
  name: string;
  current: number;
  prevPeriod: number;
  samePeriodLastYear: number;
  ytd: number;
  /** AV% — null para subtotais/memos (exibir "—"). */
  verticalPct: number | null;
}

type DreSection = 'receita' | 'deducao' | 'custoVariavel' | 'despesa' | 'financeiro' | 'investimento';

/** Classifica uma linha da DRE pela convenção de código do grupo. */
export const dreSection = (code: string): DreSection => {
  const c = code.toUpperCase();
  if (c.startsWith('RECEITA')) return 'receita';
  if (c.startsWith('DEDUCAO') || c.startsWith('IMPOSTO')) return 'deducao';
  if (c.startsWith('CUSTO')) return 'custoVariavel';
  if (c.startsWith('INVESTIMENTO')) return 'investimento';
  if (c.includes('FINANCEIRO')) return 'financeiro';
  return 'despesa'; // DESPESA_* e demais grupos operacionais
};

const sumColumns = (lines: DreLine[]) =>
  lines.reduce(
    (acc, l) => ({
      current: acc.current + l.current,
      prevPeriod: acc.prevPeriod + l.prevPeriod,
      samePeriodLastYear: acc.samePeriodLastYear + l.samePeriodLastYear,
      ytd: acc.ytd + l.ytd,
    }),
    { current: 0, prevPeriod: 0, samePeriodLastYear: 0, ytd: 0 },
  );

/**
 * Monta as linhas de exibição da DRE: linhas ordenadas por displayOrder,
 * agrupadas por seção, com subtotais injetados no estilo DRE:
 *   receitas → (=) Receita Bruta → deduções → (=) Receita Líquida →
 *   custos variáveis → (=) Margem de Contribuição → despesas → (=) EBITDA →
 *   resultado financeiro → (=) Resultado Líquido →
 *   [Fora da DRE] investimentos → (=) Investimentos.
 *
 * O valor "Atual" dos subtotais vem de `subtotals` (autoritativo da API);
 * as demais colunas são somas acumuladas das linhas exibidas acima.
 */
export const buildDreRows = (lines: DreLine[], subtotals?: DreSubtotals): DreDisplayRow[] => {
  const ordered = [...lines].sort((a, b) => a.displayOrder - b.displayOrder);

  const bySection: Record<DreSection, DreLine[]> = {
    receita: [],
    deducao: [],
    custoVariavel: [],
    despesa: [],
    financeiro: [],
    investimento: [],
  };
  for (const line of ordered) {
    bySection[dreSection(line.code)].push(line);
  }

  const rows: DreDisplayRow[] = [];
  const running = { current: 0, prevPeriod: 0, samePeriodLastYear: 0, ytd: 0 };

  const pushLines = (sectionLines: DreLine[]) => {
    for (const line of sectionLines) {
      running.current += line.current;
      running.prevPeriod += line.prevPeriod;
      running.samePeriodLastYear += line.samePeriodLastYear;
      running.ytd += line.ytd;
      rows.push({
        kind: 'line',
        code: line.code,
        name: line.name,
        current: line.current,
        prevPeriod: line.prevPeriod,
        samePeriodLastYear: line.samePeriodLastYear,
        ytd: line.ytd,
        verticalPct: line.verticalPct,
      });
    }
  };

  const pushSubtotal = (code: string, name: string, current: number | undefined) => {
    if (current === undefined) return;
    rows.push({
      kind: 'subtotal',
      code,
      name,
      current,
      prevPeriod: running.prevPeriod,
      samePeriodLastYear: running.samePeriodLastYear,
      ytd: running.ytd,
      verticalPct: null,
    });
  };

  pushLines(bySection.receita);
  pushSubtotal('RECEITA_BRUTA', '(=) Receita Bruta', subtotals?.receitaBruta);

  pushLines(bySection.deducao);
  pushSubtotal('RECEITA_LIQUIDA', '(=) Receita Líquida', subtotals?.receitaLiquida);

  pushLines(bySection.custoVariavel);
  pushSubtotal('MARGEM_CONTRIBUICAO', '(=) Margem de Contribuição', subtotals?.margemContribuicao);

  pushLines(bySection.despesa);
  pushSubtotal('EBITDA', '(=) EBITDA', subtotals?.ebitda);

  pushLines(bySection.financeiro);
  pushSubtotal('RESULTADO_LIQUIDO', '(=) Resultado Líquido', subtotals?.resultadoLiquido);

  // Investimentos — memo "Fora da DRE" (não compõem o resultado)
  if (bySection.investimento.length > 0 || (subtotals != null && subtotals.investimentos !== 0)) {
    rows.push({
      kind: 'memoHeader',
      code: 'FORA_DRE',
      name: 'Fora da DRE',
      current: 0,
      prevPeriod: 0,
      samePeriodLastYear: 0,
      ytd: 0,
      verticalPct: null,
    });
    const invTotals = sumColumns(bySection.investimento);
    pushLines(bySection.investimento);
    rows.push({
      kind: 'subtotal',
      code: 'INVESTIMENTOS',
      name: '(=) Investimentos',
      current: subtotals?.investimentos ?? Math.abs(invTotals.current),
      prevPeriod: invTotals.prevPeriod,
      samePeriodLastYear: invTotals.samePeriodLastYear,
      ytd: invTotals.ytd,
      verticalPct: null,
    });
  }

  return rows;
};

// ─── DRE — fórmula dos subtotais (memória de cálculo) ───────────

export interface DreFormulaPart {
  label: string;
  /** Valor absoluto do componente (o sinal fica em `op`). */
  value: number;
  op: '+' | '-';
}

export interface DreSubtotalFormula {
  label: string;
  parts: DreFormulaPart[];
  /** Valor autoritativo do subtotal (de `subtotals` da API). */
  result: number;
}

/** Componente da fórmula a partir do valor com sinal real. */
const formulaPart = (label: string, signed: number): DreFormulaPart => ({
  label,
  value: Math.abs(signed),
  op: signed < 0 ? '-' : '+',
});

const sectionLines = (lines: DreLine[], section: DreSection): DreLine[] =>
  lines
    .filter(l => dreSection(l.code) === section)
    .sort((a, b) => a.displayOrder - b.displayOrder);

const sectionSum = (lines: DreLine[], section: DreSection): number =>
  sectionLines(lines, section).reduce((s, l) => s + l.current, 0);

/** Componentes com valor 0 são OMITIDOS (fórmula mais enxuta). */
const nonZero = (parts: DreFormulaPart[]): DreFormulaPart[] => parts.filter(p => p.value !== 0);

/**
 * Decomposição client-side de um subtotal da DRE (linhas kind 'subtotal'
 * de `buildDreRows`) usando os valores da coluna "Atual" do relatório:
 *
 *   Receita Bruta          = Σ linhas de receita
 *   Receita Líquida        = Receita Bruta − |Deduções sobre Vendas|
 *   Margem de Contribuição = Receita Líquida − |Custos Variáveis|
 *   EBITDA                 = Margem de Contribuição − |cada linha de despesa|
 *   Resultado Líquido      = EBITDA + Resultado Financeiro
 *   Investimentos          = Σ linhas de investimento (fora da DRE)
 *
 * O `op` de cada componente vem do sinal REAL do valor (um estorno positivo
 * numa despesa vira '+'), então "result = fold(parts)" sempre fecha.
 * Componentes com valor 0 são omitidos. Código desconhecido → null.
 */
export const dreSubtotalFormula = (
  code: string,
  lines: DreLine[],
  subtotals?: DreSubtotals,
): DreSubtotalFormula | null => {
  // Sem subtotais (modo projetado) não há resultado a memorizar — o subtotal
  // sequer é renderizado, então a fórmula não tem o que abrir.
  if (!subtotals) return null;
  switch (code) {
    case 'RECEITA_BRUTA':
      return {
        label: 'Receita Bruta',
        parts: nonZero(sectionLines(lines, 'receita').map(l => formulaPart(l.name, l.current))),
        result: subtotals.receitaBruta,
      };
    case 'RECEITA_LIQUIDA':
      return {
        label: 'Receita Líquida',
        parts: nonZero([
          formulaPart('Receita Bruta', subtotals.receitaBruta),
          formulaPart('Deduções sobre Vendas', sectionSum(lines, 'deducao')),
        ]),
        result: subtotals.receitaLiquida,
      };
    case 'MARGEM_CONTRIBUICAO':
      return {
        label: 'Margem de Contribuição',
        parts: nonZero([
          formulaPart('Receita Líquida', subtotals.receitaLiquida),
          formulaPart('Custos Variáveis', sectionSum(lines, 'custoVariavel')),
        ]),
        result: subtotals.margemContribuicao,
      };
    case 'EBITDA':
      return {
        label: 'EBITDA',
        parts: nonZero([
          formulaPart('Margem de Contribuição', subtotals.margemContribuicao),
          ...sectionLines(lines, 'despesa').map(l => formulaPart(l.name, l.current)),
        ]),
        result: subtotals.ebitda,
      };
    case 'RESULTADO_LIQUIDO':
      return {
        label: 'Resultado Líquido',
        parts: nonZero([
          formulaPart('EBITDA', subtotals.ebitda),
          formulaPart('Resultado Financeiro', subtotals.resultadoFinanceiro),
        ]),
        result: subtotals.resultadoLiquido,
      };
    case 'INVESTIMENTOS':
      return {
        label: 'Investimentos',
        parts: nonZero(sectionLines(lines, 'investimento').map(l => formulaPart(l.name, l.current))),
        result: subtotals.investimentos,
      };
    default:
      return null;
  }
};

// ─── Receitas — gráfico de vendas diárias ───────────────────────

export interface RevenueDailyChartRow {
  /** Rótulo do eixo X ("01".."31"). */
  day: string;
  total: number;
  site: number;
  seller: number;
  po: number;
  firstPurchase: number;
  recurring: number;
  /** Margem bruta do dia. */
  earn: number;
  /** Acumulados até o dia (chave `<métrica>Acc`, para as linhas do gráfico). */
  totalAcc: number;
  siteAcc: number;
  sellerAcc: number;
  poAcc: number;
  firstPurchaseAcc: number;
  recurringAcc: number;
  earnAcc: number;
}

export interface RevenueDailyInput {
  day: number;
  total: number;
  site: number;
  seller: number;
  po: number;
  firstPurchase: number;
  recurring: number;
  earn: number;
}

/**
 * Converte as vendas diárias em linhas de gráfico Recharts (uma linha por
 * dia, ordenadas por dia, com o rótulo do eixo X zero-padded). Cada métrica
 * vira uma chave da linha (barras do valor diário) e ganha também a versão
 * acumulada até o dia em `<métrica>Acc` (linhas de acumulado do gráfico).
 */
export const revenueDailyChartRows = (dailySales: RevenueDailyInput[]): RevenueDailyChartRow[] => {
  const acc = { total: 0, site: 0, seller: 0, po: 0, firstPurchase: 0, recurring: 0, earn: 0 };
  return [...dailySales]
    .sort((a, b) => a.day - b.day)
    .map(d => {
      acc.total += d.total;
      acc.site += d.site;
      acc.seller += d.seller;
      acc.po += d.po;
      acc.firstPurchase += d.firstPurchase;
      acc.recurring += d.recurring;
      acc.earn += d.earn;
      return {
        day: String(d.day).padStart(2, '0'),
        total: d.total,
        site: d.site,
        seller: d.seller,
        po: d.po,
        firstPurchase: d.firstPurchase,
        recurring: d.recurring,
        earn: d.earn,
        totalAcc: acc.total,
        siteAcc: acc.site,
        sellerAcc: acc.seller,
        poAcc: acc.po,
        firstPurchaseAcc: acc.firstPurchase,
        recurringAcc: acc.recurring,
        earnAcc: acc.earn,
      };
    });
};

/**
 * Versão ANUAL do gráfico de receitas: cada "dia" é um mês (day = 1..12,
 * date = "yyyy-MM-01"). Mesmas métricas/acumulados de `revenueDailyChartRows`,
 * mas o rótulo do eixo X vira o mês abreviado pt-BR ("jan/26", ...).
 */
export const revenueAnnualChartRows = (
  dailySales: (RevenueDailyInput & { date: string })[],
): RevenueDailyChartRow[] => {
  const sorted = [...dailySales].sort((a, b) => a.day - b.day);
  return revenueDailyChartRows(sorted).map((row, idx) => ({
    ...row,
    day: monthLabelPtBR(sorted[idx].date.slice(0, 7)),
  }));
};

// ─── Receitas — top produtos das categorias ─────────────────────

export interface TopProductRow {
  sku: string;
  description: string;
  value: number;
  qty: number;
}

/**
 * Achata os produtos de todas as categorias e retorna os `n` maiores pelo
 * critério escolhido (`value` ou `qty`), em ordem decrescente. Empates são
 * resolvidos pela outra métrica (desc) e, por fim, pela descrição (asc)
 * para manter a ordenação estável/determinística.
 */
export const topProductsFromCategories = (
  categories: { products: TopProductRow[] }[],
  n: number,
  by: 'value' | 'qty',
): TopProductRow[] => {
  const other: 'value' | 'qty' = by === 'value' ? 'qty' : 'value';
  return categories
    .flatMap(c => c.products)
    .sort((a, b) => b[by] - a[by] || b[other] - a[other] || a.description.localeCompare(b.description))
    .slice(0, Math.max(0, n));
};

export interface TopCategoryRow {
  description: string;
  value: number;
  qty: number;
}

/**
 * Retorna as `n` maiores categorias pelo critério escolhido (`value` ou
 * `qty`), em ordem decrescente, com o mesmo desempate determinístico de
 * `topProductsFromCategories` (outra métrica desc, depois descrição asc).
 */
export const topCategories = (
  categories: TopCategoryRow[],
  n: number,
  by: 'value' | 'qty',
): TopCategoryRow[] => {
  const other: 'value' | 'qty' = by === 'value' ? 'qty' : 'value';
  return [...categories]
    .sort((a, b) => b[by] - a[by] || b[other] - a[other] || a.description.localeCompare(b.description))
    .slice(0, Math.max(0, n));
};

// ─── Fluxo de caixa — lançamentos do dia ────────────────────────

/**
 * Lançamentos de um dia dentro da(s) conta(s) filtrada(s) no relatório.
 *
 * O casamento é por `idAccountsMain` — a MESMA chave que agrupa os totais
 * diários. Casar por nome não funciona: contas de empresas diferentes se
 * chamam "Conta Corrente", e a coluna denormalizada que a API devolvia antes
 * (`FinancialTransaction.AccountsDescription`) está vazia desde 2025.
 *
 * `selectedIds` vazio = consolidado (todas as contas).
 */
export const cashflowDayTransactions = <T extends { date: string; idAccountsMain: number }>(
  transactions: T[],
  date: string,
  selectedIds: ReadonlySet<number>,
): T[] =>
  transactions.filter(t => t.date === date && (selectedIds.size === 0 || selectedIds.has(t.idAccountsMain)));

/**
 * Chave estável de uma linha do detalhamento por dia.
 *
 * A linha PREVISTA vem da agenda de recebíveis, não de FinancialTransaction:
 * ela não tem `transactionId` e todas chegam com 0. Sem uma chave própria, duas
 * bandeiras previstas no mesmo dia colidiriam no React.
 */
export const cashflowRowKey = (t: {
  transactionId: number;
  date: string;
  idAccountsMain: number;
  description: string;
  projected?: boolean;
}): string =>
  t.projected ? `p|${t.date}|${t.idAccountsMain}|${t.description}` : `t|${t.transactionId}`;

/**
 * Subtítulo da linha prevista: quantas parcelas o crédito reúne e, quando a
 * data já passou, o aviso de que ele não foi conciliado. Previsão não tem
 * categoria nem plano de contas — esses campos só existem para lançamento real.
 */
export const projectedRowDetail = (t: { overdue?: boolean; projectedItems?: unknown[] }): string => {
  const n = t.projectedItems?.length ?? 0;
  const parcelas = n === 1 ? '1 parcela' : `${n} parcelas`;
  return t.overdue ? `${parcelas} · venceu e não foi conciliado` : `${parcelas} · a receber`;
};

// ─── Gráficos — altura dinâmica ─────────────────────────────────

/**
 * Altura (px) de um BarChart vertical que escala com o nº de linhas,
 * garantindo espaço legível por barra (mínimo 280px).
 */
export const verticalBarChartHeight = (rowCount: number): number =>
  Math.max(280, rowCount * 28 + 40);

// ─── Despesas — histórico 12 meses ──────────────────────────────

/**
 * Retorna os ids dos `n` maiores grupos por valor absoluto do mês corrente
 * (seleção default do multi-select da evolução 12 meses).
 */
export const defaultTopGroups = <T extends { dreGroupId: number; total: number }>(
  groups: T[],
  n: number,
): number[] =>
  [...groups]
    .sort((a, b) => Math.abs(b.total) - Math.abs(a.total))
    .slice(0, n)
    .map(g => g.dreGroupId);

export interface HistoryChartRow {
  /** Rótulo do eixo X (jun/26). */
  month: string;
  /** yyyy-MM original (para ordenação/tooltip). */
  monthKey: string;
  [seriesLabel: string]: string | number;
}

/**
 * Converte a série do histórico em linhas de gráfico Recharts:
 * uma chave por grupo selecionado (label do grupo), valores em módulo
 * (despesas chegam negativas da API).
 */
export const historyToChartData = (
  series: { month: string; values: Record<string, number> }[],
  labels: Record<string, string>,
  selectedIds: number[],
): HistoryChartRow[] =>
  series.map(point => {
    const row: HistoryChartRow = { month: monthLabelPtBR(point.month), monthKey: point.month };
    for (const id of selectedIds) {
      const key = String(id);
      row[labels[key] ?? key] = Math.abs(point.values[key] ?? 0);
    }
    return row;
  });
